import React, { useEffect, useState } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes } from 'react-router-dom';

import './App.css';
import LoginForm from './components/Auth/LoginForm';
import SignupForm from './components/Auth/SignupForm';
import HomePage from './components/Home/HomePage';
import DashboardPage from './components/Dashboard/DashboardPage';
import SearchPage from './components/Search/SearchPage';
import ChatbotPage from './components/Chatbot/ChatbotPage';
import RiskMapPage from './components/RiskMap/RiskMapPage';
import AnalyticsPage from './components/Analytics/AnalyticsPage';
import AboutPage from './components/About/AboutPage';
import ProfilePage from './components/Profile/ProfilePage';
import SettingsPage from './components/Settings/SettingsPage';
import ProtectedLayout from './components/Layout/ProtectedLayout';
import ChatbotWidget from './components/Chatbot/ChatbotWidget';
import { AuthProvider } from './contexts/AuthContext';
import api from './services/api';

function App() {
  const currentYear = new Date().getFullYear();
  const contactEmail = 'hello@ecopredict.example';
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSubmitStatus, setContactSubmitStatus] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  useEffect(() => {
    if (!contactModalOpen) {
      return;
    }

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setContactModalOpen(false);
      }
    };

    window.addEventListener('keydown', onEscape);
    return () => {
      window.removeEventListener('keydown', onEscape);
    };
  }, [contactModalOpen]);

  const closeContactModal = () => {
    setContactModalOpen(false);
    setContactSubmitStatus(null);
  };

  const formatContactError = (error: any): string => {
    const detail = error?.response?.data?.detail;

    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }

    if (Array.isArray(detail) && detail.length > 0) {
      const messages = detail
        .map((item) => {
          if (typeof item === 'string') {
            return item;
          }

          if (item && typeof item === 'object') {
            const fieldPath = Array.isArray(item.loc) ? item.loc.slice(1).join('.') : '';
            const message = typeof item.msg === 'string' ? item.msg : 'Invalid value';
            return fieldPath ? `${fieldPath}: ${message}` : message;
          }

          return '';
        })
        .filter(Boolean);

      if (messages.length > 0) {
        return messages.join(' | ');
      }
    }

    if (typeof error?.message === 'string' && error.message.trim()) {
      return error.message;
    }

    return 'Unable to send message right now. Please try again.';
  };

  const handleContactSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setContactSubmitStatus(null);
    setContactSubmitting(true);

    try {
      const response = await api.post('/contact', {
        name: contactForm.name,
        email: contactForm.email,
        subject: contactForm.subject,
        message: contactForm.message,
      });

      if (!response?.data?.id) {
        throw new Error('Contact API response did not include a saved record id.');
      }

      setContactSubmitStatus({
        type: 'success',
        text: response?.data?.message || 'Message sent successfully.',
      });
      setContactForm({ name: '', email: '', subject: '', message: '' });
    } catch (error: any) {
      setContactSubmitStatus({
        type: 'error',
        text: formatContactError(error),
      });
    } finally {
      setContactSubmitting(false);
    }
  };

  const footerSocialLinks = [
    { label: 'GitHub', href: 'https://github.com/ManishKudtarkar/Eco-stat', icon: 'GH' },
    { label: 'LinkedIn', href: 'https://www.linkedin.com/search/results/all/?keywords=Manish%20Kudtarkar', icon: 'in' },
    { label: 'X', href: 'https://x.com/search?q=EcoPredict', icon: 'X' },
  ];

  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginForm />} />
          <Route path="/signup" element={<SignupForm />} />
          <Route element={<ProtectedLayout />}>
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="search" element={<SearchPage />} />
            <Route path="chatbot" element={<ChatbotPage />} />
            <Route path="risk-map" element={<RiskMapPage />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="about" element={<AboutPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        <footer className="app-footer" aria-label="Site footer">
          <div className="app-footer-inner">
            <div className="app-footer-brand">
              <span className="app-footer-kicker">EcoPredict</span>
              <p>
                Tracking biodiversity risk with live environmental intelligence, species insights,
                and early-warning monitoring.
              </p>
              <div className="app-footer-social" aria-label="Social links">
                {footerSocialLinks.map((item) => (
                  <a key={item.label} href={item.href} target="_blank" rel="noreferrer" aria-label={item.label}>
                    <span aria-hidden="true">{item.icon}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="app-footer-links" aria-label="Quick links">
              <Link to="/dashboard">Dashboard</Link>
              <Link to="/search">Search</Link>
              <Link to="/risk-map">Risk Map</Link>
              <Link to="/analytics">Analytics</Link>
              <Link to="/about">About</Link>
            </div>

            <div className="app-footer-cta">
              <p className="app-footer-cta-title">Stay close to the signal</p>
              <p>
                Follow live biodiversity updates, ecological risk summaries, and the newest research signals.
              </p>
              <button type="button" className="app-footer-cta-link" onClick={() => setContactModalOpen(true)}>
                Contact the team
              </button>
              <p className="app-footer-meta-line">Live RSS news, GBIF records, and climate signals.</p>
              <p className="app-footer-meta-line">© {currentYear} EcoPredict. All rights reserved.</p>
            </div>
          </div>
        </footer>

        {/* Chatbot widget — visible on every page */}
        <ChatbotWidget />

        {contactModalOpen && (
          <div className="app-contact-modal" role="dialog" aria-modal="true" aria-labelledby="contact-team-title">
            <button
              type="button"
              className="app-contact-backdrop"
              aria-label="Close contact form"
              onClick={closeContactModal}
            />

            <div className="app-contact-panel">
              <div className="app-contact-header">
                <div>
                  <p className="app-contact-kicker">Contact</p>
                  <h2 id="contact-team-title">Reach the EcoPredict team</h2>
                </div>
                <button type="button" className="app-contact-close" onClick={closeContactModal} aria-label="Close contact form">
                  ×
                </button>
              </div>

              <form className="app-contact-form" onSubmit={handleContactSubmit}>
                <label>
                  Name
                  <input
                    type="text"
                    required
                    disabled={contactSubmitting}
                    value={contactForm.name}
                    onChange={(event) => setContactForm((prev) => ({ ...prev, name: event.target.value }))}
                  />
                </label>

                <label>
                  Email
                  <input
                    type="email"
                    required
                    disabled={contactSubmitting}
                    value={contactForm.email}
                    onChange={(event) => setContactForm((prev) => ({ ...prev, email: event.target.value }))}
                  />
                </label>

                <label>
                  Subject
                  <input
                    type="text"
                    required
                    disabled={contactSubmitting}
                    value={contactForm.subject}
                    onChange={(event) => setContactForm((prev) => ({ ...prev, subject: event.target.value }))}
                  />
                </label>

                <label>
                  Message
                  <textarea
                    required
                    rows={5}
                    disabled={contactSubmitting}
                    value={contactForm.message}
                    onChange={(event) => setContactForm((prev) => ({ ...prev, message: event.target.value }))}
                  />
                </label>

                {contactSubmitStatus && (
                  <p className={`app-contact-status ${contactSubmitStatus.type}`}>{contactSubmitStatus.text}</p>
                )}

                <div className="app-contact-actions">
                  <button type="button" className="app-contact-cancel" onClick={closeContactModal} disabled={contactSubmitting}>
                    Cancel
                  </button>
                  <button type="submit" className="app-contact-submit" disabled={contactSubmitting}>
                    {contactSubmitting ? 'Sending...' : 'Send message'}
                  </button>
                </div>

                <p className="app-contact-note">Messages are stored in EcoPredict support inbox (MongoDB). Backup contact: {contactEmail}</p>
              </form>
            </div>
          </div>
        )}
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
